import React from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "src/store";

import { createDeviceMdmCommand } from "@dashboard/devices/api";
import apiClient from "@dashboard/files/api/apiClient";
import ActionsMenu from "@dashboard/files/components/ActionsMenu/ActionsMenu";
import { FileActionsRow } from "@dashboard/shared/components/SidePanel/shared/FileActionsRow";
import { useCurrentSubscriptionExpired } from "@dashboard/workspaces/api/helpers";
import { selectDetailsLicense, setPaneTab } from "@devices/store";
import { Device } from "@devices/types";
import { FileInfo } from "@files/types";
import { hasUnsuccessfulAnalyzer } from "@files/utils";
import { getApkFileStatus } from "@files/utils/apkStatus";
import { PermissionService } from "@services/permissionService/permissionService";
import { SecondaryButton } from "@shared/CustomButton";
import { showErrorToast } from "@shared/toasts/Toasts";

import { getPrimaryButtonProps } from "./TableItem.utils";

type Props = {
    file: FileInfo;
    device: Device;
};
const ActionsCell = (props: Props) => {
    const dispatch = useDispatch();
    const { t } = useTranslation(["sidepanels", "common"]);

    const unsuccessfulAnalyzerExist = hasUnsuccessfulAnalyzer(props.file);
    const status = getApkFileStatus(props.file, props.device.lastState?.packages);

    const { deviceAbility } = PermissionService();
    const mayInstallApp = deviceAbility(props.device).can("install", "App");

    const isExpired = useCurrentSubscriptionExpired();
    const isOffline = !props.device.isOnline;

    const installPackageForDevice = async () => {
        try {
            const url = await apiClient.getPresignedDownloadUrl(props.file.id);
            const commandObject = {
                url: url,
                source: "url",
                package: props.file.metadata?.file?.apk?.manifest?.package,
                version: props.file.metadata?.file?.apk?.manifest?.versioncode,
            };

            await createDeviceMdmCommand({
                deviceId: props.device.id,
                json: {
                    command: "installPackage",
                    caption: "Install package",
                    ...commandObject,
                },
            }).then(() => {
                dispatch(setPaneTab("COMMANDS"));
            });
        } catch (error) {
            const err = error as Error;
            showErrorToast(err.message);
        }
    };

    const installButtonProps = getPrimaryButtonProps(
        status,
        unsuccessfulAnalyzerExist,
        mayInstallApp,
        props.file.metadata?.file?.apk?.manifest?.versioncode,
    );

    const getTooltipTitle = () => {
        if (isExpired) {
            return t("sidepanels:devicePanel.tooltip.subscriptionExpired");
        }
        if (isOffline) {
            return t("common:tooltip.deviceOffline");
        }
        return installButtonProps.tooltip;
    };

    return (
        <FileActionsRow>
            {status !== "notApk" && (
                <SecondaryButton
                    tooltipProps={{ title: getTooltipTitle() }}
                    disabled={installButtonProps.disabled || isExpired || isOffline}
                    onClick={installPackageForDevice}
                >
                    <i className={installButtonProps.iconClass} />
                </SecondaryButton>
            )}

            <ActionsMenu file={props.file} withManageTags />
        </FileActionsRow>
    );
};

export default ({ file }: { file: FileInfo }) => {
    const license = useSelector(selectDetailsLicense);

    if (!license || !license.device) {
        return null;
    }

    return <ActionsCell file={file} device={license.device} />;
};
